/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { convertToLocal, getTimeZone, isScheduled } from '../../Utilities/countdown-schedule';
import FeaturedSale from './featured-content';
import { IMfrmContentReelData } from './mfrm-content-reel.data';
import { ICountdownScheduleData, IMfrmContentReelProps } from './mfrm-content-reel.props.autogenerated';

import dayjs from 'dayjs';
import { navLinkKeywordRemoval } from '../../Utilities/plp-url-utils';
const format = 'M/D/YYYY H:m:s';

export interface IMfrmContentReelViewProps extends IMfrmContentReelProps<IMfrmContentReelData> {
    carouselItems: React.ReactNode[];
}

/**
 *
 * MfrmContentReel component
 * @extends {React.PureComponent<IMfrmContentReelProps<IMfrmContentReelData>>}
 */
class MfrmContentReel extends React.PureComponent<IMfrmContentReelProps<IMfrmContentReelData>> {
    public render(): JSX.Element | null {
        if (!this.props.config.featuredContentList) {
            return null;
        }

        const viewProps: IMfrmContentReelViewProps = {
            ...(this.props as IMfrmContentReelProps<IMfrmContentReelData>),
            carouselItems: this._getCarouselItems()
        };
        return this.props.renderView(viewProps);
    }

    private _getNewCountdown = (countdownSchedule: ICountdownScheduleData) => {
        const { enableCountdown, startDate, endDate, startTime, endTime, timeZone = '' } = countdownSchedule;

        const formattedSchedule = {
            enableCountdown,
            startDate,
            endDate,
            startTime,
            endTime,
            timeZone: getTimeZone(timeZone)
        };
        // check if countdown is scheduled to show
        if (!isScheduled(formattedSchedule, true)) {
            return;
        }
        let countdown;

        const scheduleEnd = dayjs(`${endDate} ${endTime}`, format);
        const scheduleEndLocal = scheduleEnd && convertToLocal(scheduleEnd, formattedSchedule);
        if (scheduleEndLocal) {
            countdown = scheduleEndLocal.toDate();
        }
        return countdown;
    };

    private _getCarouselItems = (): React.ReactNode[] => {
        const {
            config: { featuredContentList, salesTitle = false, enhancedEcommTracking = '', cardShape = '' },
            context: {
                request: { gridSettings }
            }
        } = this.props;

        return featuredContentList.map((sale, index) => {
            const { title: saleTitle, image, iconImage, description, promoBadge, link, countdownSchedule, tealiumEvent, tealiumPromotion, cardColor = '', titleColor = '', descriptionColor = ''} = sale;

            const countdown = countdownSchedule ? this._getNewCountdown(countdownSchedule) : undefined;

            return (
                <FeaturedSale
                    key={index}
                    carouselIndex={index}
                    gridSettings={gridSettings!}
                    title={saleTitle}
                    image={image}
                    iconImage={iconImage}
                    description={description}
                    promoBadge={promoBadge}
                    countdown={countdown}
                    linkTo={navLinkKeywordRemoval(link?.linkUrl?.destinationUrl, this.props.context.request)}
                    salesTitle={salesTitle}
                    enhancedEcommTracking={enhancedEcommTracking}
                    tealiumEvent={tealiumEvent}
                    tealiumPromotion={tealiumPromotion}
                    cardShape={cardShape}
                    cardColor={cardColor}
                    titleColor={titleColor}
                    descriptionColor={descriptionColor}
                />
            );
        });
    };
}

export default MfrmContentReel;
